import { motion } from "framer-motion";
import { useState } from "react";

interface FormData {
  name: string;
  email: string;
  phone: string;
  subject: string;
  message: string;
}

interface SubmitMessage {
  type: "success" | "error";
  message: string;
}

// Copy variations - randomly selected on mount
const COPY_VARIATIONS = [
  {
    headline: "Bis hierhin gescrollt?",
    subline: "Dein Content könnte der Nächste sein.",
  },
  {
    headline: "Ende der Galerie.",
    subline: "Anfang deiner Story.",
  },
  {
    headline: "Alle Videos gesehen?",
    subline: "Lass uns deins kreieren.",
  },
  {
    headline: "Impressed?",
    subline: "Zeit für deinen eigenen viralen Moment.",
  },
  {
    headline: "Das war's von uns.",
    subline: "Was wird's bei dir?",
  },
  {
    headline: "Du bist am Ende.",
    subline: "Dein Projekt am Anfang.",
  },
];

export default function CTAReel() {
  // Random copy selection
  const [copy] = useState(
    () => COPY_VARIATIONS[Math.floor(Math.random() * COPY_VARIATIONS.length)]
  );

  // Form states
  const [formData, setFormData] = useState<FormData>({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState<SubmitMessage | null>(
    null
  );

  // Form handlers
  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage(null);

    try {
      const response = await fetch("/api/contact", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(
          result.message || `HTTP error! status: ${response.status}`
        );
      }

      if (!result.success) {
        throw new Error(result.message || "Form submission failed");
      }

      // Success
      setSubmitMessage({
        type: "success",
        message: "Vielen Dank! Wir melden uns bald bei dir.",
      });

      // Reset form
      setFormData({
        name: "",
        email: "",
        phone: "",
        subject: "",
        message: "",
      });
    } catch (error) {
      setSubmitMessage({
        type: "error",
        message: "Etwas ist schiefgelaufen. Bitte versuche es später erneut.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      className="relative w-full flex items-center justify-center snap-start snap-always z-50"
      style={{
        height: "100dvh",
        background: "linear-gradient(135deg, #0f1114 0%, #1a1f25 100%)",
      }}
    >
      <div className="w-full max-w-md px-6 py-8 relative z-50">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="text-center mb-8"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-3">
            {copy.headline}
          </h2>
          <p className="text-lg md:text-xl text-white/70 font-light">
            {copy.subline}
          </p>
        </motion.div>

        {/* Contact Form */}
        <motion.form
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          onSubmit={handleSubmit}
          className="space-y-4"
        >
          {/* Name & Email */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-sm text-white/70" htmlFor="cta-name">
                Name *
              </label>
              <input
                id="cta-name"
                name="name"
                type="text"
                required
                value={formData.name}
                onChange={handleInputChange}
                className="w-full rounded-lg bg-white/5 border border-white/10 px-4 py-3 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-white/30 transition-all"
                placeholder="Dein Name"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm text-white/70" htmlFor="cta-email">
                E-Mail *
              </label>
              <input
                id="cta-email"
                name="email"
                type="email"
                required
                value={formData.email}
                onChange={handleInputChange}
                className="w-full rounded-lg bg-white/5 border border-white/10 px-4 py-3 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-white/30 transition-all"
                placeholder="you@example.com"
              />
            </div>
          </div>

          {/* Phone */}
          <div className="space-y-2">
            <label className="text-sm text-white/70" htmlFor="cta-phone">
              Telefon (Optional)
            </label>
            <input
              id="cta-phone"
              name="phone"
              type="tel"
              value={formData.phone}
              onChange={handleInputChange}
              className="w-full rounded-lg bg-white/5 border border-white/10 px-4 py-3 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-white/30 transition-all"
              placeholder="+49 123 4567890"
            />
          </div>

          {/* Subject */}
          <div className="space-y-2">
            <label className="text-sm text-white/70" htmlFor="cta-subject">
              Betreff *
            </label>
            <input
              id="cta-subject"
              name="subject"
              type="text"
              required
              value={formData.subject}
              onChange={handleInputChange}
              className="w-full rounded-lg bg-white/5 border border-white/10 px-4 py-3 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-white/30 transition-all"
              placeholder="Worum geht es?"
            />
          </div>

          {/* Message */}
          <div className="space-y-2">
            <label className="text-sm text-white/70" htmlFor="cta-message">
              Nachricht *
            </label>
            <textarea
              id="cta-message"
              name="message"
              required
              rows={4}
              value={formData.message}
              onChange={handleInputChange}
              className="w-full rounded-lg bg-white/5 border border-white/10 px-4 py-3 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-white/30 transition-all resize-none"
              placeholder="Schreib uns, was du dir vorstellst..."
            />
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isSubmitting}
            className={`w-full py-4 rounded-lg font-semibold transition-all ${
              isSubmitting
                ? "bg-white/50 text-black/50 cursor-not-allowed"
                : "bg-white text-black hover:bg-white/90 hover:scale-[1.02]"
            }`}
          >
            {isSubmitting ? "Senden..." : "Projekt besprechen"}
          </button>

          {/* Success/Error Message */}
          {submitMessage && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className={`p-4 rounded-lg text-sm text-center ${
                submitMessage.type === "success"
                  ? "bg-green-500/20 text-green-300 border border-green-500/30"
                  : "bg-red-500/20 text-red-300 border border-red-500/30"
              }`}
            >
              {submitMessage.message}
            </motion.div>
          )}
        </motion.form>

        {/* Back to Home Link */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="mt-8 text-center"
        >
          <a
            href="/"
            className="inline-flex items-center gap-2 text-sm text-white/60 hover:text-white transition-colors"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M19 12H5M5 12L12 19M5 12L12 5"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            Zurück zur Startseite
          </a>
        </motion.div>
      </div>
    </div>
  );
}
